import { Box, TableItem, TableRow, Typography } from '@components';
import { MonthClickPayload, MonthlyBillingResponse } from '@interfaces';
import { monthNamesShortNL } from '@utils';
import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

type Props = {
  data: MonthlyBillingResponse;
  onClick: (payload: MonthClickPayload) => void;
  title?: string;
  currency?: string; // default EUR
  className?: string;
  collapsedRows?: number; // default 3
  showDetails?: boolean;
};

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function formatCurrency(value: number, currency = 'EUR') {
  return new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value ?? 0);
}

function formatPct(p: number) {
  return new Intl.NumberFormat('nl-BE', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(p);
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function TrendPill({
  trend,
  delta,
  deltaPct,
  currency,
}: {
  trend: 'up' | 'down' | 'flat';
  delta: number;
  deltaPct: number | null;
  currency: string;
}) {
  const isUp = trend === 'up';
  const isDown = trend === 'down';

  const base =
    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium';
  const cls = isUp
    ? 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300'
    : isDown
      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
      : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';

  const icon = isUp ? '↑' : isDown ? '↓' : '→';

  return (
    <span className={`${base} ${cls}`}>
      <span className="tabular-nums">{icon}</span>
      <span className="tabular-nums">
        {delta === 0 ? '0' : formatCurrency(delta, currency)}
      </span>
      <span className="opacity-70 tabular-nums">
        {deltaPct === null ? '' : `(${formatPct(deltaPct)})`}
      </span>
    </span>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200/70 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-950">
      <Typography tag="span" type="small">
        {label}
      </Typography>
      <div className="mt-0.5 text-sm font-semibold text-slate-900 dark:text-slate-50 tabular-nums">
        {value}
      </div>
    </div>
  );
}

export default function MonthlyBillingCard({
  data,
  onClick,
  title = 'Billing per maand',
  currency = 'EUR',
  className = '',
  collapsedRows = 3,
  showDetails = true,
}: Props) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const points = useMemo(() => {
    const rows = Array.isArray(data?.results) ? [...data.results] : [];

    rows.sort((a, b) => a.year - b.year || a.month - b.month);

    const mapped = rows.map((r, i) => {
      const key = `${r.year}-${pad2(r.month)}`;
      const monthName = monthNamesShortNL[(r.month - 1) % 12] ?? `${r.month}`;
      const label = `${monthName} ${r.year}`;
      const shortLabel = `${monthName} '${String(r.year).slice(-2)}`;

      const value = Number(r['sum(`value`)'] ?? 0);
      const prevValue = i > 0 ? Number(rows[i - 1]['sum(`value`)'] ?? 0) : 0;
      const delta = value - prevValue;

      const trend: 'up' | 'down' | 'flat' =
        delta > 0.000001 ? 'up' : delta < -0.000001 ? 'down' : 'flat';

      const deltaPct = i === 0 || prevValue === 0 ? null : delta / prevValue;

      return {
        key,
        year: r.year,
        month: r.month,
        label,
        shortLabel,
        value,
        delta,
        deltaPct,
        trend,
        raw: r,
      };
    });

    // ✅ Only keep last 12 months
    return mapped.slice(-12);
  }, [data]);

  const maxValue = useMemo(
    () => points.reduce((m, p) => Math.max(m, p.value), 0),
    [points]
  );

  // ✅ Totals / KPI’s (op basis van “points”, typisch 12 maanden)
  const total = useMemo(
    () => points.reduce((s, p) => s + (p.value ?? 0), 0),
    [points]
  );
  const avg = useMemo(
    () => (points.length ? total / points.length : 0),
    [total, points.length]
  );
  const best = useMemo(() => {
    if (!points.length) return null;
    return points.reduce((a, b) => (b.value > a.value ? b : a), points[0]);
  }, [points]);

  const latest = points.length ? points[points.length - 1] : null;

  // Table: show last N by default, expandable to all
  const collapsed = useMemo(() => {
    if (points.length <= collapsedRows) return points;
    return points.slice(-collapsedRows);
  }, [points, collapsedRows]);

  const tableRows = useMemo(() => {
    const base = expanded ? points : collapsed;
    return [...base].reverse(); // ✅ newest first
  }, [expanded, points, collapsed]);

  const handlePick = (p: MonthClickPayload) => {
    setSelectedKey(p.key);
    onClick(p);
  };

  const canExpand = points.length > collapsedRows;
  const graphHeight = 180;
  const boxHeight = canExpand ? 230 : 820;

  return (
    <Box type="bordered" size="medium" gridSize="grid-1">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Typography tag="span" type="labelSemiBold">
            {title}
          </Typography>

          <div className="mt-1 flex flex-wrap items-baseline gap-3">
            <div className="text-xl font-semibold text-slate-900 dark:text-slate-50 tabular-nums">
              {formatCurrency(latest?.value ?? 0, currency)}
            </div>

            {latest && (
              <TrendPill
                trend={latest.trend}
                delta={latest.delta}
                deltaPct={latest.deltaPct}
                currency={currency}
              />
            )}
          </div>

          {latest && (
            <div className="mt-2">
              <Typography tag="span" type="label" addClass={'mr-1'}>
                Laatste maand:
              </Typography>
              <Typography tag="span" type="labelSemiBold">
                {latest.label}
              </Typography>
            </div>
          )}
        </div>

        <div className="text-xs text-slate-500 dark:text-slate-400 shrink-0">
          {points.length} maanden
        </div>
      </div>

      {/* ✅ KPI strip */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
        <Stat
          label={`Totaal (${points.length}m)`}
          value={formatCurrency(total, currency)}
        />
        <Stat
          label="Gemiddelde / maand"
          value={formatCurrency(avg, currency)}
        />
        <Stat
          label="Hoogste maand"
          value={
            best ? (
              <span className="inline-flex items-center gap-2">
                <span>{formatCurrency(best.value, currency)}</span>
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  {best.shortLabel}
                </span>
              </span>
            ) : (
              formatCurrency(0, currency)
            )
          }
        />
      </div>

      {/* Chart */}
      <div className={`mt-4 h-[${graphHeight}px]`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={points}
            margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="4 4"
              vertical={false}
              strokeOpacity={0.25}
            />
            <XAxis
              dataKey="shortLabel"
              tickLine={false}
              axisLine={false}
              fontSize={12}
              interval="preserveStartEnd"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={44}
              fontSize={12}
              tickFormatter={(v) => {
                if (maxValue >= 1000) {
                  const k = v / 1000;
                  return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
                }
                return String(v);
              }}
            />
            <Tooltip
              cursor={{ fill: 'rgba(59,130,246,0.08)' }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload as MonthClickPayload;
                return (
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-md dark:border-slate-800 dark:bg-slate-950">
                    <div className="font-medium text-slate-900 dark:text-slate-50">
                      {p.label}
                    </div>
                    <div className="text-slate-600 dark:text-slate-300 tabular-nums">
                      {formatCurrency(p.value, currency)}
                    </div>
                    <div className="mt-1">
                      <TrendPill
                        trend={p.trend}
                        delta={p.delta}
                        deltaPct={p.deltaPct}
                        currency={currency}
                      />
                    </div>
                    <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Klik om details te openen
                    </div>
                  </div>
                );
              }}
            />

            <Bar
              dataKey="value"
              radius={[10, 10, 10, 10]}
              onClick={(barData) => {
                const p = (barData as any)?.payload as MonthClickPayload;
                if (p?.key) handlePick(p);
              }}
            >
              {points.map((p) => {
                const selected = selectedKey === p.key;
                return (
                  <Cell
                    key={p.key}
                    cursor="pointer"
                    fill={
                      selected ? 'rgb(59 130 246)' : 'rgba(59,130,246,0.55)'
                    }
                    stroke={selected ? 'rgba(59,130,246,0.9)' : 'rgba(0,0,0,0)'}
                    strokeWidth={selected ? 1 : 0}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Table (collapsible) */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200/70 dark:border-slate-800">
        <div className="flex items-center justify-between bg-slate-50 px-3 py-2 dark:bg-slate-900/40">
          <div className="text-xs font-medium text-slate-600 dark:text-slate-300">
            {expanded
              ? 'Alle maanden'
              : `Laatste ${Math.min(collapsedRows, points.length)} maanden`}
          </div>

          {canExpand && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {expanded
                ? 'Toon minder'
                : `Toon meer (${points.length - collapsed.length})`}
            </button>
          )}
        </div>

        <div
          className={`transition-[max-height] duration-300 ease-in-out ${expanded ? 'max-h-[820px]' : 'max-h-[230px]'}`}
        >
          <table className="w-full text-sm">
            <thead className="text-slate-600 dark:text-slate-300">
              <TableRow className="border-b border-slate-200/70 dark:border-slate-800">
                <th className="px-3 py-2 text-left font-medium">
                  <Typography type="label" tag="span">
                    Maand
                  </Typography>
                </th>
                <th className="px-3 py-2 text-right">
                  <Typography type="label" tag="span">
                    Bedrag
                  </Typography>
                </th>
                <th className="px-3 py-2 text-right">
                  <Typography type="label" tag="span">
                    Trend
                  </Typography>
                </th>
              </TableRow>
            </thead>

            <tbody>
              {tableRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-3 py-4 text-center text-slate-500 dark:text-slate-400"
                  >
                    Geen data
                  </td>
                </tr>
              ) : (
                tableRows.map((p) => {
                  const selected = selectedKey === p.key;
                  const isLatest = p.key === latest?.key;
                  const pctOfMax =
                    maxValue > 0
                      ? clamp((p.value / maxValue) * 100, 0, 100)
                      : 0;

                  return (
                    <TableRow
                      key={p.key}
                      onClick={() => handlePick(p)}
                      className={[
                        'cursor-pointer transition-colors',
                        isLatest && 'font-semibold',
                        selected
                          ? 'bg-blue-50/70 dark:bg-blue-500/10'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-900/40',
                      ].join(' ')}
                    >
                      <TableItem className="px-3 py-2" type="none">
                        <Typography type="labelSemiBold" tag="span">
                          {p.label}
                        </Typography>

                        <div className="mt-1 h-1.5 w-full rounded-full bg-slate-200/70 dark:bg-slate-800">
                          <div
                            className="h-1.5 rounded-full bg-blue-500/70"
                            style={{ width: `${pctOfMax}%` }}
                          />
                        </div>
                      </TableItem>

                      <TableItem className="px-3 py-2 text-right tabular-nums text-slate-900 dark:text-slate-50">
                        {formatCurrency(p.value, currency)}
                      </TableItem>

                      <TableItem className="px-3 py-2 text-right">
                        <TrendPill
                          trend={p.trend}
                          delta={p.delta}
                          deltaPct={p.deltaPct}
                          currency={currency}
                        />
                      </TableItem>
                    </TableRow>
                  );
                })
              )}
            </tbody>

            {/* ✅ Footer total (compact, maar handig) */}
            {points.length > 0 && (
              <tfoot>
                <tr className="border-t border-slate-200/70 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/30">
                  <td className="px-3 py-2 text-left text-xs font-medium text-slate-600 dark:text-slate-300">
                    Totaal ({points.length}m)
                  </td>
                  <td className="px-3 py-2 text-right text-sm font-semibold text-slate-900 dark:text-slate-50 tabular-nums">
                    {formatCurrency(total, currency)}
                  </td>
                  <td className="px-3 py-2" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {showDetails ?? (
        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Tip: klik op een maand in de grafiek of tabel voor een detail
          weergave.
        </div>
      )}
    </Box>
  );
}
